import { useState } from 'react';
import { Search, X } from 'lucide-react';
import ModuleBar from './ModuleBar';
import FloatingActionButton from './FloatingActionButton';

// Une couleur par module : tuile du titre, bouton flottant. Le reste du gabarit est identique.
const THEMES = {
    emerald: { tile: 'from-emerald-500 to-teal-700' },
    blue: { tile: 'from-blue-500 to-indigo-600' },
    amber: { tile: 'from-amber-500 to-orange-600' },
    purple: { tile: 'from-violet-500 to-purple-700' },
    pink: { tile: 'from-pink-500 to-rose-600' },
    teal: { tile: 'from-teal-500 to-cyan-700' }
};

/**
 * ModulePage - Gabarit d'une page de module au standard mobile Cabaza
 * (mémoire project_design_mobile_standard) : bande logo + module sur téléphone,
 * tuile + titre + sous-titre, recherche repliable, bouton d'action flottant à 96 px
 * du bas (au-dessus du menu flottant), marge de défilement.
 *
 * @param {Object} props
 * @param {React.ComponentType} props.icon - Icône lucide du module
 * @param {string} props.title - Titre du module
 * @param {string} props.subtitle - Sous-titre (ligne grise sous le titre)
 * @param {string} props.color - Clé de THEMES (défaut: emerald)
 * @param {Function} props.onAdd - Action principale ; bouton flottant masqué si absent
 * @param {string} props.addLabel - Libellé du bouton flottant
 * @param {{value: string, onChange: Function, placeholder: string}} props.search - Recherche repliable
 * @param {React.ReactNode} props.actions - Boutons additionnels à droite du titre
 * @param {React.ReactNode} props.children - Contenu de la page
 */
const ModulePage = ({ icon: Icon, title, subtitle, color = 'emerald', onAdd, addLabel, search, actions, children }) => {
    const theme = THEMES[color] || THEMES.emerald;
    const [showSearch, setShowSearch] = useState(false);

    const toggleSearch = () => {
        if (showSearch) search?.onChange('');
        setShowSearch(!showSearch);
    };

    return (
        <div className="px-4 pb-40 sm:p-6 w-full">
            <ModuleBar icon={Icon} label={title} color={color} />

            <div className="flex items-center justify-between gap-3 pt-1 pb-4">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${theme.tile} flex items-center justify-center shrink-0`}>
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-[26px] leading-[30px] font-bold tracking-tight text-gray-900 truncate">{title}</h1>
                        {subtitle && <p className="text-sm text-gray-500 truncate">{subtitle}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {actions}
                    {search && (
                        <button
                            type="button"
                            onClick={toggleSearch}
                            aria-label={search.placeholder}
                            className="w-11 h-11 rounded-2xl border border-gray-200 bg-white flex items-center justify-center text-gray-900"
                        >
                            {showSearch ? <X className="w-5 h-5" strokeWidth={1.75} /> : <Search className="w-5 h-5" strokeWidth={1.75} />}
                        </button>
                    )}
                </div>
            </div>

            {search && showSearch && (
                <div className="mb-4 relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" strokeWidth={1.75} />
                    <input
                        type="text"
                        value={search.value}
                        onChange={(e) => search.onChange(e.target.value)}
                        placeholder={search.placeholder}
                        className="w-full h-12 pl-11 pr-4 border border-gray-200 rounded-2xl bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        autoFocus
                    />
                </div>
            )}

            {children}

            {onAdd && <FloatingActionButton onClick={onAdd} label={addLabel} color={color} />}
        </div>
    );
};

export default ModulePage;
